import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
  }

  .slice {
    cursor: pointer;
    transition: opacity 0.2s;
    stroke: white;
    stroke-width: 2px;

    &:hover {
      opacity: 0.8;
    }
  }

  .legend {
    font-family: 'Segoe UI', sans-serif;
    font-size: 12px;
  }

  .legend-item {
    cursor: pointer;
  }
`;

/**
 * D3 Pie Chart Component
 * @param {Array} data - Array of objects with format: [{label: string, value: number, ...}]
 * @param {String} labelKey - Key for labels
 * @param {String} valueKey - Key for values
 * @param {Function} onClick - Click handler receiving the clicked data item
 * @param {Array} colors - Array of colors for pie slices
 * @param {String} height - Chart height (default: 400px)
 * @param {Boolean} showLegend - Show legend (default: true)
 * @param {Boolean} showPercentages - Show percentages on slices (default: true)
 */
const D3PieChart = ({ 
  data = [], 
  labelKey = 'label', 
  valueKey = 'value',
  onClick = null,
  colors = ['#0077B5', '#00A0DC', '#27AE60', '#F39C12', '#8E44AD', '#E74C3C', '#1ABC9C', '#34495E'],
  height = '400px',
  showLegend = true,
  showPercentages = true
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: parseInt(height) });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 20, right: showLegend ? 150 : 20, bottom: 20, left: 20 };
    const width = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;
    const radius = Math.min(width, chartHeight) / 2;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left + width / 2},${margin.top + chartHeight / 2})`);

    const color = d3.scaleOrdinal()
      .domain(data.map(d => d[labelKey]))
      .range(colors);

    const pie = d3.pie()
      .value(d => d[valueKey])
      .sort(null);

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    const labelArc = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius * 0.6);

    const total = d3.sum(data, d => d[valueKey]);

    // Create pie slices
    const slices = g.selectAll('.slice')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'slice');

    slices.append('path')
      .attr('d', arc)
      .attr('fill', d => color(d.data[labelKey]))
      .style('opacity', 0)
      .on('click', (event, d) => {
        if (onClick) onClick(d.data);
      })
      .on('mouseover', (event, d) => {
        if (tooltipRef.current) {
          const percentage = ((d.data[valueKey] / total) * 100).toFixed(1);
          d3.select(tooltipRef.current)
            .style('opacity', 1)
            .html(`<strong>${d.data[labelKey]}</strong><br/>Value: ${d.data[valueKey].toLocaleString()}<br/>Percentage: ${percentage}%`)
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 20}px`);
        }
      })
      .on('mouseout', () => {
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      })
      .transition()
      .duration(800)
      .style('opacity', 1);

    // Add percentage labels on slices
    if (showPercentages) {
      slices.append('text')
        .attr('transform', d => `translate(${labelArc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .style('fill', 'white')
        .style('opacity', 0)
        .text(d => {
          const percentage = ((d.data[valueKey] / total) * 100);
          return percentage > 5 ? `${percentage.toFixed(1)}%` : '';
        })
        .transition()
        .duration(800)
        .style('opacity', 1);
    }

    // Create legend
    if (showLegend) {
      const legend = svg.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${margin.left + width + 10},${margin.top})`);

      const legendItems = legend.selectAll('.legend-item')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'legend-item')
        .attr('transform', (d, i) => `translate(0,${i * 25})`)
        .on('click', (event, d) => {
          if (onClick) onClick(d);
        });

      legendItems.append('rect')
        .attr('width', 18)
        .attr('height', 18)
        .attr('fill', d => color(d[labelKey]));

      legendItems.append('text')
        .attr('x', 24)
        .attr('y', 9)
        .attr('dy', '0.35em')
        .style('font-size', '12px')
        .text(d => {
          const label = d[labelKey];
          return label.length > 20 ? label.substring(0, 20) + '...' : label;
        });

      legendItems.append('text')
        .attr('x', 24)
        .attr('y', 9)
        .attr('dy', '0.35em')
        .attr('dx', '130')
        .style('font-size', '11px')
        .style('fill', '#666')
        .text(d => d[valueKey].toLocaleString());
    }
  }, [data, dimensions, labelKey, valueKey, onClick, colors, showLegend, showPercentages]);

  return (
    <ChartContainer ref={containerRef} height={height}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef} className="tooltip"></div>
    </ChartContainer>
  );
};

export default D3PieChart;


